import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as accountsService from '../services/accounts';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  selectedAccountPassword: null,
  isFetching: false,
  isAdding: false,
  isCreating: false,
  fetchingError: null,
  addingError: null,
  creatingUpdatingError: null,
};

const sliceName = 'accounts';

export const fetchAll = createAsyncThunk(
  `${sliceName}/fetchAll`,
  (args, thunkAPI) => {
    return accountsService.getAccounts()
      .catch((error) => {
        error.clientMessage = "Can't get accounts from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return accountsService.getAccountById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get account from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const add = createAsyncThunk(
  `${sliceName}/add`,
  (args, thunkAPI) => {
    return accountsService.postAccounts(args.formData)
      .catch((error) => {
        error.clientMessage = "Can't add accounts to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchPasswordByIdAndLogin = createAsyncThunk(
  `${sliceName}/fetchPassword`,
  (args, thunkAPI) => {
    return accountsService.getAccountPassword(args.id, args.login)
      .catch((error) => {
        error.clientMessage = "Can't get account password";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return accountsService.addAiAccount(
      args.login, args.password, args.isSuper, args.providerId
    )
      .catch((error) => {
        error.clientMessage = "Can't post ai account to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

const accounts = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {
    clearAccountPassword: (state) => {
      state.selectedAccountPassword = null;
    },
  },
  extraReducers: {
    [fetchAll.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [add.fulfilled]: (state) => {
      state.isAdding = false;
    },
    [fetchPasswordByIdAndLogin.fulfilled]: (state, action) => {
      const { password } = action.payload;
      state.selectedAccountPassword = password;
    },
    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },

    [fetchAll.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [add.pending]: (state) => {
      state.isAdding = true;
      state.addingError = null;
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },

    [fetchAll.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [add.rejected]: (state, action) => {
      state.isAdding = false;
      state.addingError = action.payload;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },
  }
});

const { reducer, actions } = accounts;

export const {
  clearAccountPassword
} = actions;

const accountsAdapterSelectors = entitiesAdapter.getSelectors((state) => state[sliceName]);

const selectedAccountsSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => accountsAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
);

export const accountsSelectors = {
  ...accountsAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isAdding: (state) => state[sliceName].isAdding,

  fetchingError: (state) => state[sliceName].fetchingError,
  addingError: (state) => state[sliceName].addingError,

  selectedAccountPassword: (state) => state[sliceName].selectedAccountPassword,

  state: (state) => state[sliceName],
  selectedAccounts: selectedAccountsSelector,
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/accountsSlice.js